import { SubscriptionDetails } from 'state/store/subscription/subscriptionStore';
import { uniquePlanIntervals } from './billing-cycle';
import type { ApiPlan, ApiPlanPrice } from '../components/subscription-plan';
import { SeatPool } from '../services/seat-service';

/**
 * One plan as the admin sees it: seats bought, held and free. A plan never bought has no pool and
 * 0 seats; buying its first seat creates the subscription at `price`.
 */
export interface PlanSeats {
  code: string;
  name: string;
  /** The catalogue plan: what it gives each person. None for a plan no longer sold. */
  plan?: ApiPlan;
  pool?: SeatPool;
  price?: ApiPlanPrice;
  itemKey: string;
  /**
   * Whether more seats can be bought on the plan held. A flat price sells "this plan for up to N
   * people", so its seats are fixed and changing its quantity would add none.
   */
  canAddSeats: boolean;
  minQuantity: number;
  maxQuantity: number | null;
  purchased: number;
  /**
   * The seats the next period starts with: the scheduled decrease when there is one, else what
   * was bought. Utilities fills no more than this, so a seat is added by raising it — free up to
   * `purchased`, since those are paid until the period ends, and charged only above that.
   */
  seatsNextPeriod: number;
  scheduledAtUtc?: string | null;
  /** Bought but its first payment is not through: nobody can be seated on it yet. */
  awaitingPayment: boolean;
  /** Where to finish that payment, while Utilities still has the page open. */
  checkoutUrl?: string | null;
  /** Cancelled: it stops at the end of the period and does not renew. */
  cancelScheduled: boolean;
  /** The last payment failed: Utilities keeps its seats but will not change its count. */
  paymentFailed: boolean;
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
  held: number;
  /** What Utilities will fill right now: the smaller of bought and scheduled, less those held. */
  available: number;
  /** Seats paid for this period and not held. All of them can be given, whatever is scheduled. */
  freeThisPeriod: number;
  unitAmountMinor?: number;
  currencyCode: string;
  interval?: string;
}

const isSeatItem = (itemKey: string) => itemKey === 'seat' || itemKey === 'user';

export const seatItemKey = (subscription: SubscriptionDetails): string =>
  (subscription.quantities?.find((q) => isSeatItem(q.itemKey)) ?? subscription.quantities?.[0])?.itemKey ??
  'seat';

/** The catalogue price for this interval, else monthly, else the first price sold. */
export const preferredPrice = (plan: ApiPlan, interval?: string): ApiPlanPrice | undefined => {
  const wanted = interval?.toLowerCase();
  if (wanted) {
    const match = plan.prices.find((price) => price.interval.toLowerCase() === wanted);
    if (match) return match;
  }
  return plan.prices.find((price) => price.interval.toLowerCase() === 'month') ?? plan.prices[0];
};

/** A first buy uses this price. A plan already held stays on its own interval. */
export const withInterval = (entry: PlanSeats, interval: string): PlanSeats => {
  if (!entry.plan || entry.pool) return entry;
  const price = preferredPrice(entry.plan, interval);
  if (!price) return entry;
  const perSeat = !price.quantityItemKey || price.quantityItemKey === entry.itemKey;
  return {
    ...entry,
    price,
    interval: price.interval,
    unitAmountMinor: perSeat ? price.unitAmountMinor : undefined,
    currencyCode: price.currencyCode || entry.currencyCode,
  };
};

const heldInterval = (plan: ApiPlan | undefined, pool: SeatPool | undefined): string | undefined =>
  pool?.subscription.interval ||
  (pool?.subscription.priceId
    ? plan?.prices.find((price) => price.priceId === pool.subscription.priceId)?.interval
    : undefined);

const toPlanSeats = (plan: ApiPlan | undefined, pool: SeatPool | undefined): PlanSeats => {
  const price = plan && preferredPrice(plan, heldInterval(plan, pool));
  const item = plan?.quantityItems.find((q) => isSeatItem(q.itemKey)) ?? plan?.quantityItems[0];
  const code = plan?.code ?? pool?.subscription.planCode ?? '';
  const status = pool?.subscription.status ?? '';
  const awaitingPayment = status === 'Incomplete';
  // Utilities changes a seat count only on a running plan; any other state refuses it.
  const quantityChangeable = !pool || status === 'Active' || status === 'Trialing';
  const itemKey = pool ? seatItemKey(pool.subscription) : item?.itemKey ?? 'seat';
  // A flat price is the whole plan's fee, not a seat's, so it is only shown when it is per seat.
  const perSeat = price ? price.quantityItemKey === itemKey : true;
  const unitAmountMinor = perSeat ? pool?.subscription.unitAmountMinor ?? price?.unitAmountMinor : undefined;
  return {
    code,
    name: pool?.subscription.planName || plan?.displayName || code,
    plan,
    pool,
    price,
    itemKey,
    canAddSeats: !pool || (perSeat && quantityChangeable && !pool.subscription.cancelAtPeriodEnd),
    minQuantity: item?.minQuantity ?? 1,
    maxQuantity: item?.maxQuantity ?? null,
    purchased: pool?.members.purchased ?? 0,
    seatsNextPeriod: pool?.members.scheduledPlaces ?? pool?.members.purchased ?? 0,
    scheduledAtUtc: pool?.members.scheduledPlaces != null ? pool.members.scheduledAtUtc ?? null : null,
    awaitingPayment,
    checkoutUrl: pool?.subscription.checkoutUrl ?? null,
    cancelScheduled: !!pool?.subscription.cancelAtPeriodEnd,
    paymentFailed: status === 'PastDue' || status === 'Unpaid',
    periodStartUtc: pool?.subscription.currentPeriodStartUtc ?? null,
    periodEndUtc: pool?.subscription.currentPeriodEndUtc ?? null,
    held: pool?.members.held ?? 0,
    available: pool?.members.available ?? 0,
    freeThisPeriod: pool && !awaitingPayment ? Math.max(0, pool.members.purchased - pool.members.held) : 0,
    unitAmountMinor: unitAmountMinor ? unitAmountMinor : undefined,
    currencyCode: pool?.subscription.currencyCode || price?.currencyCode || 'CHF',
    interval: pool?.subscription.interval ?? price?.interval,
  };
};

export const defaultSeatInterval = (entry: PlanSeats): string =>
  (entry.interval || uniquePlanIntervals(entry.plan?.prices)[0] || 'month').toLowerCase();

/** Every plan that can be sold, in catalogue order, with the organization's seats laid on top. */
export const buildPlanSeats = (plans: ApiPlan[], pools: SeatPool[]): PlanSeats[] => {
  const sellable = plans.filter((plan) => plan.prices?.length > 0);
  const poolOf = (code: string) => pools.find((pool) => pool.subscription.planCode === code);
  const offCatalogue = pools.filter((pool) => !sellable.some((plan) => plan.code === pool.subscription.planCode));
  return [
    ...sellable.map((plan) => toPlanSeats(plan, poolOf(plan.code))),
    ...offCatalogue.map((pool) => toPlanSeats(undefined, pool)),
  ];
};

export const planSeatsOfUser = (list: PlanSeats[], userId: string): PlanSeats | undefined =>
  list.find((entry) => entry.pool?.members.seats.some((seat) => seat.userId === userId));

/** How far the seat count must rise to put `count` more people on this plan. */
export const seatsToAdd = (entry: PlanSeats, count: number): number => Math.max(0, count - entry.available);

/** How many of those are new seats to pay for, beyond what is paid this period. */
export const seatsToPay = (entry: PlanSeats, count: number): number => Math.max(0, count - entry.freeThisPeriod);

/** The quantity that makes room for `count` more seats. */
export const quantityForMore = (entry: PlanSeats, count: number): number => entry.seatsNextPeriod + count;

export const formatMinor = (minor: number, currency = 'CHF'): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100);

/**
 * "CHF 45 / month" for one seat of this plan, or "CHF 45 per seat / month" with `perSeat`, or ""
 * when it carries no price.
 */
export const seatPriceLabel = (
  t: (key: string, options?: Record<string, unknown> | string) => string,
  entry: PlanSeats,
  perSeat = false
): string => {
  if (entry.unitAmountMinor == null) return '';
  const amount = formatMinor(entry.unitAmountMinor, entry.currencyCode);
  const price = perSeat ? t('PRICE_PER_SEAT_AMOUNT', { price: amount, defaultValue: '{{price}} per seat' }) : amount;
  if (!entry.interval) return price;
  const interval = entry.interval.toLowerCase();
  return `${price} / ${t(`INTERVAL_${interval.toUpperCase()}`, interval)}`;
};

export const BILLING_PROFILE_INCOMPLETE = 'subscription_billing_profile_incomplete';
export const PAYMENT_METHOD_MISSING = 'subscription_payment_method_missing';
/** A subscription still waiting for its first payment: nobody can be seated on it yet. */
export const SUBSCRIPTION_NOT_LIVE = 'subscription_not_live';

/** The code Utilities put in its error envelope, or "" when it named none. */
export const utilitiesErrorCode = (error: unknown): string => {
  const envelope = (error as { error?: { error?: { code?: string } | string } })?.error?.error;
  return typeof envelope === 'object' && envelope?.code ? envelope.code : '';
};

/**
 * The message Utilities put in its error envelope, else the error's own. An empty body comes back
 * as "{}", which says nothing, so it reads as no message and the caller's own sentence is used.
 */
export const utilitiesErrorMessage = (error: unknown): string => {
  const envelope = (error as { error?: { error?: { message?: string } | string } })?.error?.error;
  if (typeof envelope === 'string') return envelope;
  const message = envelope?.message || (error as Error)?.message || '';
  return message === '{}' ? '' : message;
};
